import "./OrderMarginLimitComponent.scss";
import template from "./OrderMarginLimitComponent.html";
import {PortfolioSummary} from "../../../../../../models/Portfolio";
import {IFormController, IOnDestroy, IOnInit} from "angular";
import {
    OrderVettingResponse,
    OutcomeType,
    VettingLevel,
    VettingMessage,
    VettingOutcome
} from "../../../../types/OrderVettingResponse";
import {CurrentUserService} from "../../../../../../../services/user/CurrentUserService";
import {Observable, ReplaySubject, Scheduler} from "rxjs";
import {ComponentCallback} from "../../../../../../../types/AngularJS";
import {Predicate, UnaryTransform} from "../../../../../../../types/GenericTypes";
import {ArrayUtil} from "../../../../../../../js/util/ArrayUtil";
import {isOpenMarketsAppBranding} from "../../../../../../../types/Branding";

export const OrderMarginLimit = "tfOrderMarginLimit";

export function orderMarginLimit() {
    return {
        template,
        require: {
            form: "^^form"
        },
        bindings: {
            portfolio: "<",
            vettingResult: "<",
            onTakeQuiz: "&"
        },
        controllerAs: "ctrl",
        controller: OrderMarginLimitComponentController
    };
}

export class OrderMarginLimitComponentController implements IOnInit, IOnDestroy {

    /** @require */
    public set form(form: IFormController | null) {
        if (form != null) {
            this.form$.next(form);
        }
    }

    private readonly form$ = new ReplaySubject<IFormController>(1);

    /** @input */
    public set portfolio(portfolio: PortfolioSummary | null) {
        if (portfolio != null) {
            this.portfolio$.next(portfolio);
        }
    }

    private readonly portfolio$ = new ReplaySubject<PortfolioSummary>(1);

    /** @input */
    public set vettingResult(result: OrderVettingResponse | null) {
        if (result != null) {
            this.vettingResult$.next(result);
        }
    }

    private readonly vettingResult$ = new ReplaySubject<OrderVettingResponse>(1);

    /** @input */
    public readonly onTakeQuiz: ComponentCallback;

    public readonly needsToTakeQuiz$: Observable<boolean>;

    public readonly errorOutcomes$: Observable<ReadonlyArray<VettingMessage>>;
    public readonly warningOutcomes$: Observable<ReadonlyArray<VettingMessage>>;

    private readonly outcomes$: Observable<ReadonlyArray<VettingOutcome>>;

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly currentUserService: CurrentUserService) {
        this.outcomes$ = this.buildOutcomes();
        this.errorOutcomes$ = this.buildErrorOutcomes();
        this.warningOutcomes$ = this.buildWarningOutcomes();
        this.needsToTakeQuiz$ = this.buildNeedsToTakeQuiz();
    }

    private buildOutcomes(): Observable<ReadonlyArray<VettingOutcome>> {
        return this.vettingResult$
                   .takeUntil(this.destroyed$)
                   .observeOn(Scheduler.async)
                   .map((result) => result.vettingOutcomes)
                   .map((o) => o.outcomes);
    }

    private buildErrorOutcomes(): Observable<ReadonlyArray<VettingMessage>> {
        return this.outcomes$
                   .observeOn(Scheduler.async)
                   .takeUntil(this.destroyed$)
                   .map(this.outcomesOfLevel(VettingLevel.ERROR))
                   .map((outcomes) => this.distinctOutcomeDescriptions(outcomes));
    }

    private buildWarningOutcomes(): Observable<ReadonlyArray<VettingMessage>> {
        return this.outcomes$
                   .observeOn(Scheduler.async)
                   .takeUntil(this.destroyed$)
                   .map(this.outcomesOfLevel(VettingLevel.WARNING))
                   .map((outcomes) => this.distinctOutcomeDescriptions(outcomes));
    }

    private outcomesOfLevel(level: VettingLevel): UnaryTransform<ReadonlyArray<VettingOutcome>> {
        return (outcomes) => outcomes.filter((o) => o.level === level);
    }

    private distinctOutcomeDescriptions(outcomes: ReadonlyArray<VettingOutcome>): ReadonlyArray<VettingMessage> {
        const descriptions = outcomes.map((o) => o.message);
        return ArrayUtil.distinct(descriptions);
    }

    private buildNeedsToTakeQuiz(): Observable<boolean> {
        const isNotOpenMarketsClient$ = this.currentUserService
                                            .current
                                            .takeUntil(this.destroyed$)
                                            .map((u) => u.branding())
                                            .map((branding) => !isOpenMarketsAppBranding(branding))
                                            .publishReplay(1)
                                            .refCount();
        const isClient$ = this.currentUserService
                              .current
                              .takeUntil(this.destroyed$)
                              .map((u) => u.isClient())
                              .publishReplay(1)
                              .refCount();
        const hasPassedQuiz$ = this.currentUserService
                                   .current
                                   .takeUntil(this.destroyed$)
                                   .map((u) => u.hasPassedMarginQuiz())
                                   .publishReplay(1)
                                   .refCount();
        const hasBaseMarginError$ = this.outcomes$
                                        .map(this.outcomesOfLevel(VettingLevel.ERROR))
                                        .map(this.hasOutcomeOfType(OutcomeType.BASE_MARGIN));

        return Observable.combineLatest(isNotOpenMarketsClient$, isClient$, hasPassedQuiz$, hasBaseMarginError$)
                         .map(([isNotOpenMarketsClient, isClient, hasPassedQuiz, hasMarginError]) => isNotOpenMarketsClient && isClient && !hasPassedQuiz && hasMarginError);
    }

    private hasOutcomeOfType(type: OutcomeType): Predicate<ReadonlyArray<VettingOutcome>> {
        return (outcomes) => outcomes.find((o) => o.outcomeType === type) != null;
    }

    public $onInit(): void {
        this.watchMarginValidationState();
    }

    private watchMarginValidationState() {
        this.errorOutcomes$.takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((errors) => errors.length > 0)
            .withLatestFrom(this.form$)
            .subscribe(([invalid, form]) => this.setMarginMovementValidationState(form, !invalid));
    }

    private setMarginLimitValidationState(form: IFormController, isError: boolean) {
        form.$setValidity("margin-limit", isError, form);
    }

    private setStelLimitValidationState(form: IFormController, isError: boolean) {
        form.$setValidity("stel-limit", isError, form);
    }

    private setMarginMovementValidationState(form: IFormController, isError: boolean) {
        form.$setValidity("margin-movement", isError, form);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
